import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  CheckCircle,
  XCircle,
  FileText,
  DollarSign,
  Package,
  RefreshCw,
  Edit2,
  Mail,
  Bell,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Plus,
  Trash2,
  Printer,
  Download,
  Send,
} from 'lucide-react';
import { adminOrders, adminShipping, adminQbo } from '../../services/api';
import { generateInvoicePdf } from '../../utils/generateInvoicePdf';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const statusBadge = (s) => {
  const map = {
    pending: 'bg-warning text-dark',
    confirmed: 'bg-info text-dark',
    shipped: 'bg-primary',
    delivered: 'bg-success',
    invoiced: 'bg-secondary',
    paid: 'bg-dark',
    cancelled: 'bg-danger',
  };
  return (
    <span className={`badge ${map[s] || 'bg-secondary'}`}>
      {s}
    </span>
  );
};

const paymentStatusBadge = (s) => {
  const map = {
    pending:             'bg-warning text-dark',
    authorized:          'bg-info text-dark',
    captured:            'bg-primary',
    invoiced:            'bg-secondary',
    paid:                'bg-success',
    failed:              'bg-danger',
    partially_refunded:  'bg-warning text-dark',
    refunded:            'bg-secondary',
    voided:              'bg-dark',
    cancelled:           'bg-danger',
  };
  const label = s ? s.replace('_', ' ') : s;
  return (
    <span className={`badge ${map[s] || 'bg-secondary'}`}>
      {label}
    </span>
  );
};

const PAYMENT_METHODS = [
  { value: 'check',       label: 'Check' },
  { value: 'wire',        label: 'Wire Transfer' },
  { value: 'ach',         label: 'ACH' },
  { value: 'cash',        label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other',       label: 'Other' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Current user decoded from JWT (for role checks)
  const [currentUser] = useState(() => {
    try {
      const token = localStorage.getItem('wholesaleToken');
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  });

  // Data
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Email actions
  const [emailModal, setEmailModal]           = useState(null); // null | 'invoice' | 'reminder' | 'overdue'
  const [emailLoading, setEmailLoading]       = useState(false);
  const [emailLog, setEmailLog]               = useState([]);
  const [emailLogOpen, setEmailLogOpen]       = useState(false);
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  // "Mark Shipped" inline form
  const [showShipForm, setShowShipForm]     = useState(false);
  const [trackingInput, setTrackingInput]   = useState('');

  // "Update Tracking" inline form
  const [showTrackingEdit, setShowTrackingEdit]     = useState(false);
  const [trackingEditInput, setTrackingEditInput]   = useState('');

  // Admin notes (debounced autosave)
  const [adminNotes, setAdminNotes] = useState('');
  const notesDebounceRef = useRef(null);
  const notesSavedRef    = useRef('');

  // Cancel modal
  const [cancelModal, setCancelModal]     = useState(false);
  const [cancelReason, setCancelReason]   = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Payment modal
  const [paymentModal, setPaymentModal]   = useState(false);
  const [paymentForm, setPaymentForm]     = useState({
    amount: '',
    paymentMethod: 'check',
    referenceNumber: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    note: '',
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Line item editing
  const [editMode, setEditMode]     = useState(false);
  const [editQtys, setEditQtys]     = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError]   = useState('');

  // Stripe refund
  const [refundModal, setRefundModal]   = useState(false);
  const [refundForm, setRefundForm]     = useState({ amount: '', reason: 'requested_by_customer', fullRefund: true });
  const [refundLoading, setRefundLoading] = useState(false);

  // QBO re-sync
  const [qboResyncLoading, setQboResyncLoading] = useState(false);
  const [qboResyncConfirm, setQboResyncConfirm] = useState(false);

  // QBO payment retry: { [paymentID]: 'loading' | 'done' | 'error' }
  const [qboPayRetryState, setQboPayRetryState] = useState({});

  // Shipping labels
  const [shippingLabels,    setShippingLabels]    = useState([]);
  const [mergedPdf,         setMergedPdf]          = useState(null);
  const [pickupConfirmation, setPickupConfirmation] = useState(null);
  const [shippingLoading,   setShippingLoading]   = useState(false);
  const [showShipModal,     setShowShipModal]      = useState(false);
  const [pickupLocations,   setPickupLocations]    = useState([]);
  const [pickupLoading,     setPickupLoading]      = useState(false);
  const [pickupError,       setPickupError]        = useState('');
  const [shipForm,          setShipForm]           = useState({
    pickupLocationIndex: 0,
    adultSignature:      true,
    shipDate:            new Date().toISOString().slice(0, 10),
  });
  const [createError,       setCreateError]        = useState('');
  const [showPickupPanel,   setShowPickupPanel]    = useState(false);
  const [pickupForm,        setPickupForm]         = useState({
    pickupLocationIndex: 0,
    pickupDate:          new Date().toISOString().slice(0, 10),
    earliestPickupTime:  '',
    latestPickupTime:    '',
  });
  const [pickupSubmitting,  setPickupSubmitting]   = useState(false);
  const [pickupSubmitError, setPickupSubmitError]  = useState('');
  const [labelEmail,        setLabelEmail]         = useState('');
  const [emailingLabels,    setEmailingLabels]     = useState(false);

  // Pickup status workflow
  const [pickupStatusLoading, setPickupStatusLoading] = useState(false);
  const [pickupStatusError,   setPickupStatusError]   = useState('');

  // Success auto-dismiss
  const successTimerRef = useRef(null);
  const showSuccess = useCallback((msg) => {
    setSuccessMsg(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSuccessMsg(''), 4000);
  }, []);

  // ── Load order ──────────────────────────────────────────────────────────────

  const loadOrder = useCallback(
    (quiet = false) => {
      if (!quiet) setLoading(true);
      setError('');
      adminOrders
        .get(id)
        .then((res) => {
          if (res.data?.success) {
            const data = res.data.data;
            setOrder(data);
            setAdminNotes(data.adminNotes || '');
            notesSavedRef.current = data.adminNotes || '';
            setTrackingEditInput(data.trackingNumber || '');
          } else {
            setError('Failed to load order.');
          }
        })
        .catch(() => {
          setError('An error occurred while loading the order.');
        })
        .finally(() => {
          if (!quiet) setLoading(false);
        });
    },
    [id]
  );

  // ── Load payments ───────────────────────────────────────────────────────────

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const res = await adminOrders.getPayments(id);
      setPayments(res?.data?.data || []);
    } catch {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  // ── Load shipping labels ─────────────────────────────────────────────────────

  const loadLabels = useCallback(async () => {
    try {
      const labelsRes = await adminShipping.getLabels(id);
      setShippingLabels(labelsRes.data.data.labels || []);
      setMergedPdf(labelsRes.data.data.mergedLabelPdfBase64 || null);
      setPickupConfirmation(labelsRes.data.data.glsPickupConfirmation || null);
    } catch {
      // Labels may not exist yet — silently ignore
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
    loadPayments();
    loadLabels();
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    };
  }, [loadOrder, loadPayments, loadLabels]);

  // ── Status transitions ──────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus, extra = {}) => {
    setActionLoading(true);
    setError('');
    try {
      await adminOrders.updateStatus(id, { status: newStatus, ...extra });
      showSuccess(`Order status updated to "${newStatus}".`);
      setShowShipForm(false);
      setTrackingInput('');
      loadOrder(true);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          `Failed to update status to "${newStatus}".`
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleShipSubmit = () => {
    if (!trackingInput.trim()) {
      setError('Please enter a tracking number before marking as shipped.');
      return;
    }
    handleStatusChange('shipped', { trackingNumber: trackingInput.trim() });
  };

  // ── Tracking update ─────────────────────────────────────────────────────────

  const handleTrackingUpdate = async () => {
    if (!trackingEditInput.trim()) {
      setError('Tracking number cannot be empty.');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminOrders.updateTracking(id, {
        trackingNumber: trackingEditInput.trim(),
      });
      showSuccess('Tracking number updated.');
      setShowTrackingEdit(false);
      loadOrder(true);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Failed to update tracking number.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ── Admin notes autosave ────────────────────────────────────────────────────

  const handleNotesChange = (e) => setAdminNotes(e.target.value);

  const handleNotesBlur = () => {
    if (adminNotes === notesSavedRef.current) return;
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      notesSavedRef.current = adminNotes;
      adminOrders
        .updateNotes(id, { adminNotes })
        .then(() => showSuccess('Notes saved.'))
        .catch(() => setError('Failed to save notes.'));
    }, 1000);
  };

  // ── Generate invoice ────────────────────────────────────────────────────────

  const handleGenerateInvoice = async () => {
    try {
      const res = await adminOrders.getInvoice(id);
      if (res.data?.success) {
        await generateInvoicePdf(res.data.data);
      } else {
        setError('Failed to fetch invoice data.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to generate invoice.');
    }
  };

  // ── Email actions ────────────────────────────────────────────────────────────

  const handleSendEmail = async () => {
    if (!emailModal) return;
    setEmailLoading(true);
    setError('');
    try {
      let result;
      if (emailModal === 'invoice') {
        result = await adminOrders.sendInvoice(id);
      } else {
        result = await adminOrders.sendReminder(id, {
          type: emailModal === 'overdue' ? 'overdue' : 'reminder',
        });
      }
      showSuccess(result?.data?.message || 'Email sent successfully.');
      setEmailModal(null);
      if (emailLogOpen) loadEmailLog();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send email.');
    } finally {
      setEmailLoading(false);
    }
  };

  const loadEmailLog = useCallback(async () => {
    setEmailLogLoading(true);
    try {
      const res = await adminOrders.getEmailLog(id);
      setEmailLog(res?.data?.data || []);
    } catch {
      setEmailLog([]);
    } finally {
      setEmailLogLoading(false);
    }
  }, [id]);

  const handleToggleEmailLog = () => {
    if (!emailLogOpen) loadEmailLog();
    setEmailLogOpen((prev) => !prev);
  };

  // ── Cancel order ─────────────────────────────────────────────────────────────

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a reason for cancellation.');
      return;
    }
    setCancelLoading(true);
    setError('');
    try {
      await adminOrders.cancelOrder(id, { reason: cancelReason.trim() });
      showSuccess('Order cancelled successfully.');
      setCancelModal(false);
      setCancelReason('');
      loadOrder(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to cancel order.');
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Record payment ───────────────────────────────────────────────────────────

  const openPaymentModal = () => {
    const remaining =
      (parseFloat(order?.totalAmount) || 0) -
      (parseFloat(order?.amountPaid) || 0);
    setPaymentForm({
      amount: remaining > 0 ? remaining.toFixed(2) : '',
      paymentMethod: 'check',
      referenceNumber: '',
      paymentDate: new Date().toISOString().slice(0, 10),
      note: '',
    });
    setPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    const amt = parseFloat(paymentForm.amount);
    if (isNaN(amt) || amt <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }
    if (!paymentForm.paymentDate) {
      setError('Please enter a payment date.');
      return;
    }
    setPaymentLoading(true);
    setError('');
    try {
      await adminOrders.recordPayment(id, paymentForm);
      showSuccess('Payment recorded successfully.');
      setPaymentModal(false);
      loadOrder(true);
      loadPayments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to record payment.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleQboResync = async () => {
    setQboResyncLoading(true);
    setError('');
    try {
      const res = await adminQbo.updateInvoice(id);
      if (res.data.success) {
        showSuccess(`QBO invoice re-synced — #${res.data.qboInvoiceNumber}`);
        loadOrder(true);
      } else {
        setError(res.data.error || 'Re-sync failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Re-sync failed');
    } finally {
      setQboResyncLoading(false);
      setQboResyncConfirm(false);
    }
  };

  const handleQboRetryPayment = async (paymentID) => {
    setQboPayRetryState((s) => ({ ...s, [paymentID]: 'loading' }));
    try {
      const res = await adminQbo.retryPayment(paymentID);
      if (res.data.success) {
        setQboPayRetryState((s) => ({ ...s, [paymentID]: 'done' }));
        loadOrder(true);
      } else {
        setQboPayRetryState((s) => ({ ...s, [paymentID]: 'error' }));
        setError(res.data.error || 'Payment sync retry failed');
      }
    } catch (err) {
      setQboPayRetryState((s) => ({ ...s, [paymentID]: 'error' }));
      setError(err.response?.data?.error || err.message || 'Payment sync retry failed');
    }
  };

  const handleDeletePayment = async (paymentID) => {
    setActionLoading(true);
    setError('');
    try {
      await adminOrders.deletePayment(id, paymentID);
      showSuccess('Payment removed.');
      loadOrder(true);
      loadPayments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to remove payment.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Line item editing ────────────────────────────────────────────────────────

  const handleEnterEditMode = () => {
    const qtys = {};
    (order?.items || []).forEach((item) => {
      qtys[item.orderItemID] = item.quantity;
    });
    setEditQtys(qtys);
    setEditError('');
    setEditMode(true);
  };

  const handleSaveItems = async () => {
    const items = (order?.items || []).map((item) => ({
      orderItemID: item.orderItemID,
      quantity: parseInt(editQtys[item.orderItemID] ?? item.quantity, 10),
    }));
    if (items.some((i) => isNaN(i.quantity) || i.quantity < 1)) {
      setEditError('All quantities must be at least 1.');
      return;
    }
    setEditLoading(true);
    setEditError('');
    setError('');
    try {
      await adminOrders.editItems(id, { items });
      showSuccess('Line items updated.');
      setEditMode(false);
      setEditQtys({});
      loadOrder(true);
    } catch (err) {
      setEditError(err?.response?.data?.message || 'Failed to update items.');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Stripe refund ────────────────────────────────────────────────────────────

  const openRefundModal = () => {
    setRefundForm({
      amount: parseFloat(order?.totalAmount || 0).toFixed(2),
      reason: 'requested_by_customer',
      fullRefund: true,
    });
    setRefundModal(true);
  };

  const handleIssueRefund = async () => {
    const amt = parseFloat(refundForm.amount);
    if (isNaN(amt) || amt <= 0) {
      setError('Please enter a valid refund amount.');
      return;
    }
    setRefundLoading(true);
    setError('');
    try {
      const res = await adminOrders.issueRefund(id, {
        amount: amt,
        reason: refundForm.reason,
      });
      showSuccess(res?.data?.message || `Refund of $${amt.toFixed(2)} processed.`);
      setRefundModal(false);
      loadOrder(true);
      loadPayments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Refund failed.');
    } finally {
      setRefundLoading(false);
    }
  };

  // ── Shipping helpers ─────────────────────────────────────────────────────────

  function downloadPdf(base64, filename) {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Generates 30-min increment time slot options from 7:00 am to 8:00 pm
  // Returns array of { label: '7:00 am', value: '07:00 am' }
  function getTimeSlotOptions() {
    const slots = [];
    for (let h = 7; h <= 20; h++) {
      for (let m = 0; m < 60; m += 30) {
        if (h === 20 && m > 0) break;
        const period = h < 12 ? 'am' : 'pm';
        const displayH = h > 12 ? h - 12 : h;
        const paddedH  = String(h).padStart(2, '0');
        const paddedM  = String(m).padStart(2, '0');
        const label = `${displayH}:${paddedM} ${period}`;
        const value = `${paddedH}:${paddedM} ${period}`;
        slots.push({ label, value });
      }
    }
    return slots;
  }

  // Converts API time strings like "08:00 AM" or "8:00 AM" to GLS format "08:00 am"
  function formatToGlsTime(timeStr) {
    if (!timeStr) return '';
    try {
      const clean = timeStr.trim();
      const match = clean.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
      if (!match) return '';
      const h = String(parseInt(match[1], 10)).padStart(2, '0');
      const m = match[2];
      const p = match[3].toLowerCase();
      return `${h}:${m} ${p}`;
    } catch {
      return '';
    }
  }

  async function handleOpenShipModal() {
    setShowShipModal(true);
    setCreateError('');
    setPickupError('');
    setPickupLoading(true);
    try {
      const res = await adminShipping.getPickupLocations();
      setPickupLocations(res.data.data || []);
    } catch (err) {
      setPickupError(
        err.response?.data?.message ||
        (err.response?.status === 400
          ? 'GLS credentials not configured. Go to Settings → Shipping.'
          : 'Failed to load pickup locations.')
      );
    } finally {
      setPickupLoading(false);
    }
  }

  async function handleCreateLabels() {
    setShippingLoading(true);
    setCreateError('');
    try {
      const res = await adminShipping.createLabels(id, {
        pickupLocationIndex: shipForm.pickupLocationIndex,
        adultSignature:      shipForm.adultSignature,
        shipDate:            shipForm.shipDate,
      });
      setShippingLabels(res.data.data.labels);
      setMergedPdf(res.data.data.mergedLabelPdfBase64);
      setShowShipModal(false);
      await loadOrder();
      await loadLabels();
      showSuccess('Labels created successfully.');
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create labels.');
    } finally {
      setShippingLoading(false);
    }
  }

  async function handleEmailLabels() {
    setEmailingLabels(true);
    try {
      await adminShipping.emailLabels(id, labelEmail || undefined);
      showSuccess('Labels emailed successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to email labels.');
    } finally {
      setEmailingLabels(false);
    }
  }

  async function handleOpenPickupPanel() {
    setShowPickupPanel(true);
    setPickupSubmitError('');
    if (pickupLocations.length === 0) {
      setPickupLoading(true);
      try {
        const res = await adminShipping.getPickupLocations();
        const locs = res.data.data || [];
        setPickupLocations(locs);
        if (locs[0]) {
          setPickupForm(f => ({
            ...f,
            earliestPickupTime: formatToGlsTime(locs[0].EarliestPickupTime),
            latestPickupTime:   formatToGlsTime(locs[0].LatestPickupTime),
          }));
        }
      } catch {
        setPickupSubmitError('Failed to load pickup locations.');
      } finally {
        setPickupLoading(false);
      }
    }
  }

  async function handleRequestPickup() {
    setPickupSubmitting(true);
    setPickupSubmitError('');
    try {
      const res = await adminShipping.requestPickup(id, pickupForm);
      setPickupConfirmation(res.data.data.confirmationNumber);
      setShowPickupPanel(false);
      showSuccess(`Pickup scheduled — Confirmation #${res.data.data.confirmationNumber}`);
    } catch (err) {
      setPickupSubmitError(err.response?.data?.message || 'Failed to schedule pickup.');
    } finally {
      setPickupSubmitting(false);
    }
  }

  function handlePickupLocationChange(index) {
    const loc = pickupLocations[index];
    setPickupForm(f => ({
      ...f,
      pickupLocationIndex:  index,
      earliestPickupTime:   loc ? formatToGlsTime(loc.EarliestPickupTime) : f.earliestPickupTime,
      latestPickupTime:     loc ? formatToGlsTime(loc.LatestPickupTime)   : f.latestPickupTime,
    }));
  }

  // ── Pickup status workflow ────────────────────────────────────────────────
  async function handleUpdatePickupStatus(newStatus) {
    setPickupStatusLoading(true);
    setPickupStatusError('');
    try {
      await adminOrders.updatePickupStatus(id, newStatus);
      showSuccess(
        newStatus === 'ready'
          ? 'Order marked ready — customer notified by email.'
          : 'Order marked as picked up.'
      );
      loadOrder();
    } catch (err) {
      setPickupStatusError(err.response?.data?.message || 'Failed to update pickup status.');
    } finally {
      setPickupStatusLoading(false);
    }
  }

  function getBoxSummary() {
    if (!order?.items) return { total: 0, standard: 0, port: 0, totalWeight: 0 };
    let standard = 0, port = 0;
    for (const item of order.items) {
      const qty = parseInt(item.quantity) || 0;
      if ((item.varietal || '').toLowerCase().includes('port')) {
        port += qty;
      } else {
        standard += qty;
      }
    }
    return {
      total:       standard + port,
      standard,
      port,
      totalWeight: standard * 42 + port * 28,
    };
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderTransitionButtons = () => {
    if (!order) return null;
    const transitions = order.allowedTransitions || [];

    return (
      <div className="d-flex flex-wrap gap-2 align-items-start">
        {/* Confirm Order: pending → confirmed */}
        {transitions.includes('confirmed') && (
          <button
            className="btn btn-info btn-sm d-flex align-items-center gap-1"
            onClick={() => handleStatusChange('confirmed')}
            disabled={actionLoading}
          >
            <CheckCircle size={15} />
            Confirm Order
          </button>
        )}

        {/* Mark Shipped: confirmed → shipped */}
        {transitions.includes('shipped') && !showShipForm && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => setShowShipForm(true)}
            disabled={actionLoading}
          >
            <Truck size={15} />
            Mark Shipped
          </button>
        )}

        {/* Inline ship form */}
        {transitions.includes('shipped') && showShipForm && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 220 }}
              placeholder="Tracking number"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleShipSubmit()}
              autoFocus
            />
            <button
              className="btn btn-primary btn-sm d-flex align-items-center gap-1"
              onClick={handleShipSubmit}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                <Truck size={15} />
              )}
              Confirm Ship
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => { setShowShipForm(false); setTrackingInput(''); }}
              disabled={actionLoading}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Mark Delivered: shipped → delivered */}
        {transitions.includes('delivered') && (
          <button
            className="btn btn-success btn-sm d-flex align-items-center gap-1"
            onClick={() => handleStatusChange('delivered')}
            disabled={actionLoading}
          >
            <Package size={15} />
            Mark Delivered
          </button>
        )}

        {/* Send Invoice: delivered → invoiced */}
        {transitions.includes('invoiced') && order.paymentType === 'invoice' && (
          <button
            className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
            onClick={() => handleStatusChange('invoiced')}
            disabled={actionLoading}
          >
            <FileText size={15} />
            Send Invoice
          </button>
        )}

        {/* Mark Paid */}
        {transitions.includes('paid') && (
          <button
            className="btn btn-dark btn-sm d-flex align-items-center gap-1"
            onClick={() => handleStatusChange('paid')}
            disabled={actionLoading}
          >
            <DollarSign size={15} />
            Mark Paid
          </button>
        )}

        {/* Cancel Order — opens modal */}
        {transitions.includes('cancelled') && (
          <button
            className="btn btn-danger btn-sm d-flex align-items-center gap-1"
            onClick={() => { setCancelReason(''); setCancelModal(true); }}
            disabled={actionLoading}
          >
            <XCircle size={15} />
            Cancel Order
          </button>
        )}

        {actionLoading && !showShipForm && (
          <span
            className="spinner-border spinner-border-sm text-secondary ms-1"
            role="status"
          />
        )}
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading order…</span>
        </div>
      </div>
    );
  }

  if (!order && !loading) {
    return (
      <div className="container-fluid py-4">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <button
          className="btn btn-link p-0 d-flex align-items-center gap-1"
          onClick={() => navigate('/admin/orders')}
        >
          <ArrowLeft size={16} />
          Back to Orders
        </button>
      </div>
    );
  }

  const isInvoice   = order.paymentType === 'invoice';
  const isCancelled = order.orderStatus === 'cancelled';
  const canEdit     = ['pending', 'confirmed'].includes(order.orderStatus);
  const totalAmount = parseFloat(order.totalAmount) || 0;
  const amountPaid  = parseFloat(order.amountPaid)  || 0;
  const remaining   = totalAmount - amountPaid;
  const paidPct     = totalAmount > 0 ? Math.min(100, (amountPaid / totalAmount) * 100) : 0;

  return (
    <div className="container-fluid py-4">
      {/* Back link */}
      <button
        className="btn btn-link p-0 mb-3 d-flex align-items-center gap-1 text-decoration-none"
        onClick={() => navigate('/admin/orders')}
      >
        <ArrowLeft size={16} />
        Back to Orders
      </button>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          />
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMsg}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMsg('')}
            aria-label="Close"
          />
        </div>
      )}

      {/* ── Cancelled banner ─────────────────────────────────────────────────── */}
      {isCancelled && (
        <div className="alert alert-danger d-flex align-items-start gap-2 mb-4" role="alert">
          <XCircle size={18} className="flex-shrink-0 mt-1" />
          <div>
            <strong>This order has been cancelled.</strong>
            {order.cancelledAt && (
              <span className="ms-2 text-muted small">
                {new Date(order.cancelledAt).toLocaleString()}
              </span>
            )}
            {order.cancelledByName && (
              <span className="ms-2 text-muted small">by {order.cancelledByName}</span>
            )}
            {order.cancelReason && (
              <div className="mt-1 small">Reason: {order.cancelReason}</div>
            )}
          </div>
        </div>
      )}

      {/* ── QBO Sync Status Card ────────────────────────────────────────────── */}
      {(order.qbo_sync_status || order.qbo_invoice_id || order.qbo_void_status) && (
        <div className={`card mb-4 border-${
          order.qbo_sync_status === 'synced'   ? 'success' :
          order.qbo_sync_status === 'failed'   ? 'danger'  :
          order.qbo_hold                       ? 'warning' : 'secondary'
        }`}>
          <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <FileText size={15} />
              <strong>QuickBooks Status</strong>
              {order.qbo_sync_status === 'synced'  && <span className="badge bg-success">Synced</span>}
              {order.qbo_sync_status === 'pending' && !order.qbo_hold && <span className="badge bg-warning text-dark">Pending</span>}
              {order.qbo_sync_status === 'pending' && order.qbo_hold  && <span className="badge bg-warning text-dark">Held — No QBO Customer ID</span>}
              {order.qbo_sync_status === 'failed'  && <span className="badge bg-danger">Sync Failed</span>}
              {order.qbo_void_status === 'voided'             && <span className="badge bg-secondary ms-1">Invoice Voided</span>}
              {order.qbo_void_status === 'credit_memo_issued' && <span className="badge bg-info ms-1">Credit Memo Issued</span>}
              {order.qbo_void_status === 'failed'             && <span className="badge bg-danger ms-1">Void/Credit Failed</span>}
            </div>
            {/* Re-sync button — only when a QBO invoice exists */}
            {order.qbo_invoice_id && order.companyQboCustomerId && !qboResyncConfirm && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setQboResyncConfirm(true)}
              >
                Re-sync to QBO
              </button>
            )}
            {qboResyncConfirm && (
              <div className="d-flex align-items-center gap-2">
                <small className="text-warning fw-semibold">This will update the invoice in QBO.</small>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleQboResync}
                  disabled={qboResyncLoading}
                >
                  {qboResyncLoading
                    ? <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
                    : 'Confirm Re-sync'}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setQboResyncConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="card-body py-2">
            <div className="row g-2 small">
              {order.qbo_invoice_number && (
                <div className="col-auto">
                  <span className="text-muted">Invoice #:</span>{' '}
                  <strong>{order.qbo_invoice_number}</strong>
                </div>
              )}
              {order.qbo_terms && (
                <div className="col-auto">
                  <span className="text-muted">Terms:</span>{' '}
                  <strong>{order.qbo_terms}</strong>
                </div>
              )}
              {order.qbo_synced_at && (
                <div className="col-auto">
                  <span className="text-muted">Last synced:</span>{' '}
                  {formatDate(order.qbo_synced_at)}
                </div>
              )}
            </div>
            {order.qbo_sync_error && order.qbo_sync_status === 'failed' && (
              <div className="alert alert-danger py-1 px-2 mb-0 mt-2 small">
                {order.qbo_sync_error}
              </div>
            )}
            {order.qbo_hold && (
              <div className="alert alert-warning py-1 px-2 mb-0 mt-2 small">
                Sync is held because no QBO Customer ID is assigned to this company.
                <a href={`/admin/customers/${order.customerID}`} className="ms-2">
                  Assign one on the customer page
                </a>{' '}then retry from the QBO dashboard.
              </div>
            )}
            {order.qbo_void_status === 'failed' && (
              <div className="alert alert-danger py-1 px-2 mb-0 mt-2 small">
                Could not void the QBO invoice or create a credit memo automatically.
                Manual action is required in QuickBooks.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Order Header Card ───────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">
              {order.qbo_invoice_number
                ? <>Invoice #{order.qbo_invoice_number}</>
                : <>Order #{order.orderID}</>}
            </h5>
            {statusBadge(order.orderStatus)}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => loadOrder(true)}
              disabled={actionLoading}
              title="Refresh"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              onClick={handleGenerateInvoice}
            >
              <FileText size={14} />
              Generate Invoice
            </button>
            <button
              className="btn btn-outline-dark btn-sm d-flex align-items-center gap-1"
              onClick={() => setEmailModal('invoice')}
              title="Email invoice to customer"
            >
              <Mail size={14} />
              Email Invoice
            </button>
            {order.paymentStatus === 'invoiced' && (
              <div className="dropdown">
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <Bell size={14} />
                  Send Reminder
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => setEmailModal('reminder')}
                    >
                      Payment Reminder
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => setEmailModal('overdue')}
                    >
                      Overdue Notice
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="row g-3">
            {/* Customer info */}
            <div className="col-12 col-md-4">
              <p className="text-muted small mb-1">Customer</p>
              <p className="mb-0 fw-semibold">{order.companyName || '—'}</p>
              {order.customerEmail && (
                <p className="mb-0 small text-muted">{order.customerEmail}</p>
              )}
              {order.customerPhone && (
                <p className="mb-0 small text-muted">{order.customerPhone}</p>
              )}
            </div>

            {/* Order date & amounts */}
            <div className="col-12 col-md-4">
              <p className="text-muted small mb-1">Order Date</p>
              <p className="mb-1">{formatDate(order.orderDate)}</p>
              <p className="text-muted small mb-1">Total Amount</p>
              <p className="mb-0 fw-semibold fs-5">
                {formatCurrency(order.totalAmount)}
              </p>
            </div>

            {/* Payment info */}
            <div className="col-12 col-md-4">
              <p className="text-muted small mb-1">Payment</p>
              <p className="mb-1 text-capitalize">
                Type:{' '}
                <span className="fw-semibold">{order.paymentType || '—'}</span>
                {isInvoice && order.netDays != null && (
                  <span className="text-muted ms-1">(Net {order.netDays})</span>
                )}
              </p>
              <p className="mb-0">
                Status: {paymentStatusBadge(order.paymentStatus)}
              </p>
            </div>
          </div>

          {/* Shipped / delivered dates */}
          {(order.shippedDate || order.deliveredDate) && (
            <div className="row g-3 mt-1 pt-2 border-top">
              {order.shippedDate && (
                <div className="col-auto">
                  <span className="text-muted small">Shipped: </span>
                  <span className="small">{formatDate(order.shippedDate)}</span>
                </div>
              )}
              {order.deliveredDate && (
                <div className="col-auto">
                  <span className="text-muted small">Delivered: </span>
                  <span className="small">{formatDate(order.deliveredDate)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Status Workflow ─────────────────────────────────────────────────── */}
      {order.allowedTransitions && order.allowedTransitions.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Status Actions</h6>
          </div>
          <div className="card-body">{renderTransitionButtons()}</div>
        </div>
      )}

      {/* ── Fulfillment ─────────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">Fulfillment</h6>
        </div>
        <div className="card-body">
          {(!order.fulfillmentType || order.fulfillmentType === 'ship') && (
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="badge bg-primary">Ship</span>
                <span className="text-muted small">Shipped to customer's address on file.</span>
              </div>
              {order.shipping_cost > 0 && (
                <div className="mt-2 small">
                  <span className="text-muted">Shipping Cost:</span>{' '}
                  <strong>{formatCurrency(order.shipping_cost)}</strong>
                  {order.shipping_rule_description && (
                    <span className="text-muted ms-2">({order.shipping_rule_description})</span>
                  )}
                </div>
              )}
            </div>
          )}
          {order.fulfillmentType === 'pickup' && (
            <div>
              {/* Status row */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="badge bg-success">Pickup</span>
                {(!order.pickup_status || order.pickup_status === 'pending') && (
                  <span className="badge bg-secondary">Pending</span>
                )}
                {order.pickup_status === 'ready' && (
                  <span className="badge bg-info">Ready for Pickup</span>
                )}
                {order.pickup_status === 'picked_up' && (
                  <span className="badge bg-dark">Picked Up</span>
                )}
              </div>

              {/* Location details — prefer new admin-managed location */}
              {(order.newPickupLocationName || order.pickupLocationName) ? (
                <div className="mb-3 small">
                  <div className="fw-semibold">
                    {order.newPickupLocationName || order.pickupLocationName}
                  </div>
                  {order.newPickupLocationAddress && (
                    <div className="text-muted">
                      {[
                        order.newPickupLocationAddress,
                        order.newPickupLocationAddress2,
                        order.newPickupLocationCity,
                        order.newPickupLocationState,
                        order.newPickupLocationZip,
                      ].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {!order.newPickupLocationAddress && (order.pickupLocationAddress || order.pickupLocationCity) && (
                    <div className="text-muted">
                      {[order.pickupLocationAddress, order.pickupLocationCity, order.pickupLocationState].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {order.newPickupInstructions && (
                    <div className="mt-1 text-muted fst-italic">{order.newPickupInstructions}</div>
                  )}
                  {order.newPickupLeadDays > 0 && (
                    <div className="mt-1">
                      <span className="badge bg-light text-dark border">
                        Lead time: {order.newPickupLeadDays} day{order.newPickupLeadDays !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted small">No pickup location on file.</p>
              )}

              {order.requestedPickupDate && (
                <div className="mb-3 small text-muted">
                  Requested Date:{' '}
                  <strong>
                    {new Date(order.requestedPickupDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </strong>
                </div>
              )}

              {/* Workflow buttons */}
              {pickupStatusError && (
                <div className="alert alert-danger py-2 small mb-2">{pickupStatusError}</div>
              )}
              <div className="d-flex gap-2 flex-wrap">
                {(!order.pickup_status || order.pickup_status === 'pending') && (
                  <button
                    className="btn btn-success btn-sm"
                    disabled={pickupStatusLoading}
                    onClick={() => handleUpdatePickupStatus('ready')}
                  >
                    {pickupStatusLoading
                      ? <span className="spinner-border spinner-border-sm me-1" />
                      : <CheckCircle size={14} className="me-1" />
                    }
                    Mark Ready for Pickup
                  </button>
                )}
                {order.pickup_status === 'ready' && (
                  <button
                    className="btn btn-dark btn-sm"
                    disabled={pickupStatusLoading}
                    onClick={() => handleUpdatePickupStatus('picked_up')}
                  >
                    {pickupStatusLoading
                      ? <span className="spinner-border spinner-border-sm me-1" />
                      : <CheckCircle size={14} className="me-1" />
                    }
                    Mark Picked Up
                  </button>
                )}
                {order.pickup_status === 'picked_up' && (
                  <span className="text-muted small align-self-center">
                    <CheckCircle size={14} className="me-1 text-success" />
                    Pickup complete
                  </span>
                )}
              </div>
            </div>
          )}
          {order.fulfillmentType === 'delivery' && (
            <div>
              <span className="badge bg-warning text-dark me-2">Delivery</span>
              <span className="text-muted small">Local delivery — contact customer to confirm details.</span>
              {order.deliveryNotes && (
                <div className="mt-2 border rounded p-2 bg-light small">
                  <div className="fw-semibold mb-1">Delivery Notes</div>
                  {order.deliveryNotes}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Invoice Details (invoice customers) ─────────────────────────────── */}
      {isInvoice && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Invoice Details</h6>
          </div>
          <div className="card-body">
            {/* Payment progress bar */}
            {totalAmount > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Payment Progress</span>
                  <span>{paidPct.toFixed(0)}% paid</span>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div
                    className={`progress-bar ${paidPct >= 100 ? 'bg-success' : 'bg-primary'}`}
                    style={{ width: `${paidPct}%` }}
                  />
                </div>
              </div>
            )}
            <div className="row g-3">
              <div className="col-auto">
                <p className="text-muted small mb-1">Order Total</p>
                <p className="mb-0 fw-semibold">{formatCurrency(order.totalAmount)}</p>
              </div>
              <div className="col-auto">
                <p className="text-muted small mb-1">Amount Paid</p>
                <p className="mb-0 fw-semibold text-success">
                  {formatCurrency(amountPaid)}
                </p>
              </div>
              <div className="col-auto">
                <p className="text-muted small mb-1">Balance Remaining</p>
                <p className={`mb-0 fw-semibold ${remaining > 0 ? 'text-danger' : 'text-success'}`}>
                  {formatCurrency(remaining)}
                </p>
              </div>
              <div className="col-auto">
                <p className="text-muted small mb-1">Due Date</p>
                <p className="mb-0 fw-semibold">{formatDate(order.invoiceDueDate)}</p>
              </div>
              {order.invoicePaidDate && (
                <div className="col-auto">
                  <p className="text-muted small mb-1">Paid Date</p>
                  <p className="mb-0 fw-semibold">{formatDate(order.invoicePaidDate)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Payment Ledger (invoice customers, not cancelled) ────────────────── */}
      {isInvoice && !isCancelled && (
        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <DollarSign size={15} />
              <h6 className="mb-0">Payment Ledger</h6>
            </div>
            {order.paymentStatus !== 'paid' && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center gap-1"
                onClick={openPaymentModal}
                disabled={actionLoading}
              >
                <Plus size={14} />
                Record Payment
              </button>
            )}
          </div>
          <div className="card-body p-0">
            {paymentsLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-muted" role="status" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center text-muted py-4 small">
                No payments recorded yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Recorded By</th>
                      <th>Note</th>
                      <th>QBO</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.paymentID}>
                        <td>{formatDate(p.paymentDate)}</td>
                        <td className="fw-semibold text-success">
                          {formatCurrency(p.amount)}
                        </td>
                        <td className="text-capitalize">
                          {p.paymentMethod.replace('_', ' ')}
                        </td>
                        <td>{p.referenceNumber || '—'}</td>
                        <td>{p.recordedByName || '—'}</td>
                        <td>{p.note || '—'}</td>
                        <td>
                          {p.qbo_payment_sync_status === 'synced' && (
                            <span className="badge bg-success" title={`QBO Payment ID: ${p.qbo_payment_id}`}>Synced</span>
                          )}
                          {p.qbo_payment_sync_status === 'failed' && (
                            <div>
                              <span className="badge bg-danger mb-1">Failed</span>
                              {currentUser?.role === 'admin' && order.qbo_invoice_id && (
                                <div>
                                  <button
                                    className="btn btn-outline-primary btn-sm py-0 px-1 mt-1"
                                    onClick={() => handleQboRetryPayment(p.paymentID)}
                                    disabled={qboPayRetryState[p.paymentID] === 'loading'}
                                    title="Retry QBO payment sync"
                                  >
                                    {qboPayRetryState[p.paymentID] === 'loading'
                                      ? <span className="spinner-border spinner-border-sm" />
                                      : <RefreshCw size={11} />}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {!p.qbo_payment_sync_status && order.qbo_invoice_id && (
                            <span className="text-muted small">—</span>
                          )}
                          {!order.qbo_invoice_id && (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                        <td className="text-end">
                          {currentUser?.role === 'admin' &&
                            order.paymentStatus !== 'paid' && (
                              <button
                                className="btn btn-outline-danger btn-sm py-0 px-1"
                                onClick={() => handleDeletePayment(p.paymentID)}
                                disabled={actionLoading}
                                title="Remove payment"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td className="text-end fw-bold small">Total Paid</td>
                      <td className="fw-bold text-success">
                        {formatCurrency(amountPaid)}
                      </td>
                      <td colSpan={6}></td>
                    </tr>
                    {remaining > 0 && (
                      <tr>
                        <td className="text-end fw-bold small">Remaining</td>
                        <td className="fw-bold text-danger">
                          {formatCurrency(remaining)}
                        </td>
                        <td colSpan={6}></td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Stripe Info (CC customers) ──────────────────────────────────────── */}
      {!isInvoice && order.stripePaymentIntentID && (
        <div className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h6 className="mb-0">Stripe Payment</h6>
            {['captured', 'paid'].includes(order.paymentStatus) && (
              <button
                className="btn btn-outline-warning btn-sm d-flex align-items-center gap-1"
                onClick={openRefundModal}
                disabled={actionLoading}
              >
                <DollarSign size={13} />
                Issue Refund
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-auto">
                <p className="text-muted small mb-1">Payment Intent ID</p>
                <p className="mb-0 font-monospace small">
                  {order.stripePaymentIntentID}
                </p>
              </div>
              {order.stripeIntent && (
                <>
                  <div className="col-auto">
                    <p className="text-muted small mb-1">Intent Status</p>
                    <p className="mb-0">
                      <span className="badge bg-secondary">
                        {order.stripeIntent.status}
                      </span>
                    </p>
                  </div>
                  <div className="col-auto">
                    <p className="text-muted small mb-1">Intent Amount</p>
                    <p className="mb-0">
                      {order.stripeIntent.amount != null
                        ? formatCurrency(order.stripeIntent.amount / 100)
                        : '—'}
                    </p>
                  </div>
                </>
              )}
              {['partially_refunded', 'refunded'].includes(order.paymentStatus) && (
                <div className="col-auto">
                  <p className="text-muted small mb-1">Amount Refunded</p>
                  <p className="mb-0 fw-semibold text-warning">
                    {formatCurrency(Math.abs(amountPaid - totalAmount))}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tracking Number ─────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h6 className="mb-0">Tracking</h6>
          {!showTrackingEdit && (
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => {
                setTrackingEditInput(order.trackingNumber || '');
                setShowTrackingEdit(true);
              }}
            >
              <Edit2 size={13} />
              Update Tracking
            </button>
          )}
        </div>
        <div className="card-body">
          {!showTrackingEdit ? (
            <p className="mb-0">
              {order.trackingNumber ? (
                <span className="font-monospace">{order.trackingNumber}</span>
              ) : (
                <span className="text-muted">No tracking number yet.</span>
              )}
            </p>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 280 }}
                placeholder="Enter tracking number"
                value={trackingEditInput}
                onChange={(e) => setTrackingEditInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTrackingUpdate()}
                autoFocus
              />
              <button
                className="btn btn-primary btn-sm"
                onClick={handleTrackingUpdate}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  'Save'
                )}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowTrackingEdit(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Line Items ──────────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h6 className="mb-0">Line Items</h6>
          {canEdit && !editMode && (
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={handleEnterEditMode}
            >
              <Edit2 size={13} />
              Edit Items
            </button>
          )}
          {editMode && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleSaveItems}
                disabled={editLoading}
              >
                {editLoading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  'Save Changes'
                )}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { setEditMode(false); setEditQtys({}); setEditError(''); }}
                disabled={editLoading}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="card-body p-0">
          {editError && (
            <div className="alert alert-warning m-3 mb-0 py-2 d-flex align-items-center gap-2">
              <AlertCircle size={15} />
              {editError}
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th>Vintage</th>
                  <th>Varietal</th>
                  <th className="text-center">Qty</th>
                  <th className="text-end">Unit Price</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{item.name || '—'}</td>
                      <td>{item.vintage || '—'}</td>
                      <td>{item.varietal || '—'}</td>
                      <td className="text-center">
                        {editMode ? (
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            style={{ width: 70, margin: '0 auto' }}
                            min={1}
                            value={editQtys[item.orderItemID] ?? item.quantity}
                            onChange={(e) =>
                              setEditQtys((prev) => ({
                                ...prev,
                                [item.orderItemID]: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          item.quantity
                        )}
                      </td>
                      <td className="text-end">{formatCurrency(item.pricePerUnit)}</td>
                      <td className="text-end fw-semibold">
                        {editMode
                          ? formatCurrency(
                              (parseFloat(item.pricePerUnit) || 0) *
                                (parseInt(editQtys[item.orderItemID] ?? item.quantity, 10) || 0)
                            )
                          : formatCurrency(item.totalPrice)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      No line items found.
                    </td>
                  </tr>
                )}
              </tbody>
              {order.items && order.items.length > 0 && (
                <tfoot className="table-light">
                  <tr>
                    <td colSpan={5} className="text-end fw-bold">
                      Order Total
                    </td>
                    <td className="text-end fw-bold">
                      {editMode
                        ? formatCurrency(
                            order.items.reduce((sum, item) => {
                              const qty = parseInt(editQtys[item.orderItemID] ?? item.quantity, 10) || 0;
                              return sum + (parseFloat(item.pricePerUnit) || 0) * qty;
                            }, 0)
                          )
                        : formatCurrency(order.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* ── Shipping Labels ──────────────────────────────────────────────────── */}
      {['confirmed','shipped','delivered','invoiced','paid'].includes(order.orderStatus) && (
        (order.fulfillmentType && order.fulfillmentType !== 'ship') ? (
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center gap-2">
              <Truck size={16} />
              <h6 className="mb-0">Shipping Labels</h6>
            </div>
            <div className="card-body text-muted small">
              No shipping label required for{' '}
              <strong>{order.fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}</strong> orders.
            </div>
          </div>
        ) : (() => {
        const boxSummary  = getBoxSummary();
        const timeSlots   = getTimeSlotOptions();
        const hasLabels   = shippingLabels.length > 0;
        const totalCharge = shippingLabels.reduce((s, l) => s + parseFloat(l.shippingCharge || 0), 0);
        const today       = new Date().toISOString().slice(0, 10);
        const maxDate     = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const selectedPickupLoc = pickupLocations[pickupForm.pickupLocationIndex] || null;

        return (
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                {hasLabels
                  ? <CheckCircle size={16} className="text-success" />
                  : <Truck size={16} />
                }
                <h6 className="mb-0">Shipping Labels</h6>
              </div>
              {hasLabels && (
                <span className="badge bg-secondary">
                  {shippingLabels.length} package{shippingLabels.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="card-body">
              {/* No labels yet — show summary + create button */}
              {!hasLabels && order.orderStatus === 'confirmed' && (
                <>
                  <p className="text-muted small mb-3">
                    {boxSummary.total} cases total
                    {boxSummary.standard > 0 && ` — ${boxSummary.standard} standard (42 lbs each)`}
                    {boxSummary.port > 0 && `, ${boxSummary.port} port (28 lbs each)`}
                  </p>
                  <button
                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                    onClick={handleOpenShipModal}
                  >
                    <Truck size={14} />
                    Create Shipping Labels
                  </button>
                </>
              )}

              {/* Labels already exist */}
              {hasLabels && (
                <>
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Tracking Number</th>
                          <th>Type</th>
                          <th className="text-end">Charge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shippingLabels.map((lbl, i) => (
                          <tr key={lbl.labelID}>
                            <td>{i + 1}</td>
                            <td>
                              <a
                                href={`https://www.gls-us.com/track/?trackingNumber=${encodeURIComponent(lbl.trackingNumber)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-monospace small"
                              >
                                {lbl.trackingNumber}
                              </a>
                            </td>
                            <td>
                              <span className={`badge ${lbl.isPort ? 'bg-secondary' : 'bg-primary'}`}>
                                {lbl.isPort ? 'Port' : 'Standard'}
                              </span>
                            </td>
                            <td className="text-end">{formatCurrency(lbl.shippingCharge)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan={3} className="text-end fw-bold small">Total Shipping Cost</td>
                          <td className="text-end fw-bold">{formatCurrency(totalCharge)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <hr className="my-3" />

                  {/* Actions row */}
                  <div className="d-flex flex-wrap align-items-start gap-3">
                    {mergedPdf && (
                      <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                        onClick={() => downloadPdf(mergedPdf, `Labels-Order-${id}.pdf`)}
                      >
                        <Download size={14} />
                        Download All Labels
                      </button>
                    )}

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 220 }}
                        placeholder="recipient@email.com"
                        value={labelEmail}
                        onChange={e => setLabelEmail(e.target.value)}
                      />
                      <button
                        className="btn btn-outline-dark btn-sm d-inline-flex align-items-center gap-1"
                        onClick={handleEmailLabels}
                        disabled={emailingLabels}
                      >
                        {emailingLabels
                          ? <span className="spinner-border spinner-border-sm" />
                          : <Send size={13} />
                        }
                        Email Labels
                      </button>
                    </div>
                  </div>

                  <hr className="my-3" />

                  {/* Pickup section */}
                  {pickupConfirmation ? (
                    <div>
                      <div className="alert alert-success py-2 mb-2">
                        <CheckCircle size={14} className="me-1" />
                        <strong>Pickup Scheduled — Confirmation #{pickupConfirmation}</strong>
                      </div>
                      <p className="text-muted small mb-0">
                        {shippingLabels.length} package{shippingLabels.length !== 1 ? 's' : ''} scheduled for pickup
                      </p>
                    </div>
                  ) : (
                    <div>
                      {!showPickupPanel ? (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleOpenPickupPanel}
                        >
                          Schedule GLS Pickup
                        </button>
                      ) : (
                        <div className="border rounded p-3 bg-light">
                          <h6 className="fw-semibold mb-3 small">Schedule On-Call Pickup</h6>

                          {/* Pickup location */}
                          <div className="mb-3">
                            <label className="form-label form-label-sm">Pickup Location</label>
                            {pickupLoading ? (
                              <div className="d-flex align-items-center gap-2 text-muted small">
                                <span className="spinner-border spinner-border-sm" />
                                Loading locations…
                              </div>
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={pickupForm.pickupLocationIndex}
                                onChange={e => handlePickupLocationChange(parseInt(e.target.value, 10))}
                              >
                                {pickupLocations.map((loc, i) => (
                                  <option key={i} value={i}>
                                    {loc.PickupCompanyName || loc.PickupAddress1}
                                    {' — '}
                                    {loc.PickupAddress1}, {loc.PickupCity}
                                    {loc.LatestCallInByTime ? ` (Latest call-in: ${loc.LatestCallInByTime})` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>

                          {/* Pickup date */}
                          <div className="mb-3">
                            <label className="form-label form-label-sm">Pickup Date</label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={pickupForm.pickupDate}
                              min={today}
                              max={maxDate}
                              onChange={e => setPickupForm(f => ({ ...f, pickupDate: e.target.value }))}
                            />
                          </div>

                          {/* Time selects */}
                          <div className="row g-2 mb-3">
                            <div className="col-6">
                              <label className="form-label form-label-sm">Packages Ready At</label>
                              <select
                                className="form-select form-select-sm"
                                value={pickupForm.earliestPickupTime}
                                onChange={e => setPickupForm(f => ({ ...f, earliestPickupTime: e.target.value }))}
                              >
                                <option value="">-- select --</option>
                                {timeSlots.map(s => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-6">
                              <label className="form-label form-label-sm">Latest Pickup Time</label>
                              <select
                                className="form-select form-select-sm"
                                value={pickupForm.latestPickupTime}
                                onChange={e => setPickupForm(f => ({ ...f, latestPickupTime: e.target.value }))}
                              >
                                <option value="">-- select --</option>
                                {timeSlots.map(s => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {pickupSubmitError && (
                            <div className="alert alert-danger py-2 small mb-3">{pickupSubmitError}</div>
                          )}

                          <p className="text-muted small mb-3">
                            Scheduling a pickup notifies GLS to collect{' '}
                            {shippingLabels.length} package{shippingLabels.length !== 1 ? 's' : ''}{' '}
                            from the selected location.
                          </p>

                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => { setShowPickupPanel(false); setPickupSubmitError(''); }}
                              disabled={pickupSubmitting}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                              onClick={handleRequestPickup}
                              disabled={
                                pickupSubmitting ||
                                !pickupForm.pickupDate ||
                                !pickupForm.earliestPickupTime ||
                                !pickupForm.latestPickupTime ||
                                !selectedPickupLoc
                              }
                            >
                              {pickupSubmitting && <span className="spinner-border spinner-border-sm" />}
                              Request Pickup
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })())
      }

      {/* ── Create Labels Modal ───────────────────────────────────────────────── */}
      {showShipModal && (() => {
        const boxSummary = getBoxSummary();
        const today      = new Date().toISOString().slice(0, 10);
        const maxDate    = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

        return (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: 'rgba(0,0,0,.5)' }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <Truck size={18} />
                    Create Shipping Labels — Order #{id}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => { setShowShipModal(false); setCreateError(''); }}
                    disabled={shippingLoading}
                  />
                </div>

                <div className="modal-body">
                  {/* Package Summary */}
                  <h6 className="fw-semibold mb-2">Package Summary</h6>
                  <div className="table-responsive mb-4">
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th className="text-center">Cases</th>
                          <th>Type</th>
                          <th>Weight Each</th>
                          <th className="text-end">Total Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(order?.items || []).map((item, i) => {
                          const isPort = (item.varietal || '').toLowerCase().includes('port');
                          const wt = isPort ? 28 : 42;
                          return (
                            <tr key={i}>
                              <td className="small">{item.name || '—'}</td>
                              <td className="text-center">{item.quantity}</td>
                              <td>
                                <span className={`badge ${isPort ? 'bg-secondary' : 'bg-primary'}`}>
                                  {isPort ? 'Port' : 'Standard'}
                                </span>
                              </td>
                              <td className="small">{wt} lbs</td>
                              <td className="text-end small">
                                {(parseInt(item.quantity) || 0) * wt} lbs
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan={4} className="fw-semibold small">
                            Total: {boxSummary.total} box{boxSummary.total !== 1 ? 'es' : ''}
                          </td>
                          <td className="text-end fw-semibold small">{boxSummary.totalWeight} lbs</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Pickup Location */}
                  <h6 className="fw-semibold mb-2">Pickup Location</h6>
                  {pickupLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted small mb-4">
                      <span className="spinner-border spinner-border-sm" />
                      Loading pickup locations…
                    </div>
                  ) : pickupError ? (
                    <div className="alert alert-danger py-2 small mb-4 d-flex align-items-center justify-content-between">
                      <span>{pickupError}</span>
                      <button
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={handleOpenShipModal}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <select
                        className="form-select"
                        value={shipForm.pickupLocationIndex}
                        onChange={e => setShipForm(f => ({ ...f, pickupLocationIndex: parseInt(e.target.value, 10) }))}
                      >
                        {pickupLocations.map((loc, i) => (
                          <option key={i} value={i}>
                            {loc.PickupCompanyName || loc.PickupAddress1}
                            {' — '}
                            {loc.PickupAddress1}, {loc.PickupCity}
                            {loc.LatestCallInByTime ? ` (Pickup by ${loc.LatestCallInByTime})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Delivery Options */}
                  <h6 className="fw-semibold mb-2">Delivery Options</h6>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="adultSigCheck"
                        checked={shipForm.adultSignature}
                        onChange={e => setShipForm(f => ({ ...f, adultSignature: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="adultSigCheck">
                        Require Adult Signature
                      </label>
                    </div>
                    <div className="form-text">Recommended for all alcohol shipments</div>
                  </div>
                  <div className="mb-4">
                    <label className="form-label">Ship Date</label>
                    <input
                      type="date"
                      className="form-control"
                      style={{ maxWidth: 200 }}
                      value={shipForm.shipDate}
                      min={today}
                      max={maxDate}
                      onChange={e => setShipForm(f => ({ ...f, shipDate: e.target.value }))}
                    />
                  </div>

                  <div className="alert alert-warning py-2 small">
                    <AlertCircle size={14} className="me-1" />
                    Generating labels will automatically mark this order as <strong>Shipped</strong> and cannot be undone.
                  </div>

                  {createError && (
                    <div className="alert alert-danger py-2 small mt-2">{createError}</div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowShipModal(false); setCreateError(''); }}
                    disabled={shippingLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    onClick={handleCreateLabels}
                    disabled={pickupLoading || !!pickupError || shippingLoading || boxSummary.total === 0}
                  >
                    {shippingLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <Printer size={15} />
                    }
                    {shippingLoading
                      ? 'Generating…'
                      : `Generate ${boxSummary.total} Label${boxSummary.total !== 1 ? 's' : ''}`
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Admin Notes ─────────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">Admin Notes</h6>
        </div>
        <div className="card-body">
          <textarea
            className="form-control"
            rows={4}
            placeholder="Internal notes (autosaved on blur)…"
            value={adminNotes}
            onChange={handleNotesChange}
            onBlur={handleNotesBlur}
          />
          <p className="text-muted small mt-1 mb-0">
            Notes are saved automatically 1 second after you stop typing and
            leave the field.
          </p>
        </div>
      </div>

      {/* ── Email History Accordion ──────────────────────────────────────────── */}
      <div className="card mb-4">
        <div
          className="card-header d-flex align-items-center justify-content-between"
          style={{ cursor: 'pointer' }}
          onClick={handleToggleEmailLog}
        >
          <div className="d-flex align-items-center gap-2">
            <Mail size={15} />
            <h6 className="mb-0">Email History</h6>
            {emailLog.length > 0 && (
              <span className="badge bg-secondary">{emailLog.length}</span>
            )}
          </div>
          {emailLogOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {emailLogOpen && (
          <div className="card-body p-0">
            {emailLogLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-muted" />
              </div>
            ) : emailLog.length === 0 ? (
              <div className="text-center text-muted py-4 small">
                No emails have been sent for this order yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: 12 }}>Date</th>
                      <th style={{ fontSize: 12 }}>Type</th>
                      <th style={{ fontSize: 12 }}>Recipient</th>
                      <th style={{ fontSize: 12 }}>Subject</th>
                      <th style={{ fontSize: 12 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLog.map((log) => (
                      <tr key={log.logID}>
                        <td style={{ fontSize: 12 }}>
                          {new Date(log.sentAt).toLocaleString()}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <span
                            className={`badge ${
                              log.emailType === 'invoice'      ? 'bg-primary' :
                              log.emailType === 'reminder'     ? 'bg-info text-dark' :
                              log.emailType === 'overdue'      ? 'bg-warning text-dark' :
                              log.emailType === 'confirmation' ? 'bg-success' :
                              'bg-secondary'
                            }`}
                          >
                            {log.emailType}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>{log.recipient}</td>
                        <td style={{ fontSize: 12, maxWidth: 260 }}>
                          <span
                            className="d-inline-block text-truncate"
                            style={{ maxWidth: 240 }}
                            title={log.subject}
                          >
                            {log.subject}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {log.status === 'sent' ? (
                            <span className="badge bg-success">Sent</span>
                          ) : (
                            <span
                              className="badge bg-danger"
                              title={log.errorMsg || 'Unknown error'}
                              style={{ cursor: 'help' }}
                            >
                              Failed
                              {log.errorMsg && (
                                <AlertCircle size={11} className="ms-1" />
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Email Confirm Modal ──────────────────────────────────────────────── */}
      {emailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => e.target === e.currentTarget && setEmailModal(null)}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {emailModal === 'invoice'  && 'Email Invoice'}
                  {emailModal === 'reminder' && 'Send Payment Reminder'}
                  {emailModal === 'overdue'  && 'Send Overdue Notice'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEmailModal(null)}
                  disabled={emailLoading}
                />
              </div>
              <div className="modal-body">
                {emailModal === 'invoice' && (
                  <p>
                    Send the invoice for Order #{order.orderID} to{' '}
                    <strong>{order.customerEmail}</strong>?
                    <br />
                    <span className="text-muted small">
                      The email will include order details and payment instructions.
                    </span>
                  </p>
                )}
                {emailModal === 'reminder' && (
                  <p>
                    Send a payment reminder for Order #{order.orderID} to{' '}
                    <strong>{order.customerEmail}</strong>?
                    <br />
                    <span className="text-muted small">
                      Due date: {order.invoiceDueDate
                        ? new Date(order.invoiceDueDate).toLocaleDateString()
                        : '—'}
                    </span>
                  </p>
                )}
                {emailModal === 'overdue' && (
                  <p>
                    Send an overdue notice for Order #{order.orderID} to{' '}
                    <strong>{order.customerEmail}</strong>?
                    <br />
                    <span className="text-muted small">
                      This email uses firm but professional language to prompt payment.
                    </span>
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setEmailModal(null)}
                  disabled={emailLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleSendEmail}
                  disabled={emailLoading}
                >
                  {emailLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <Mail size={15} />
                  )}
                  {emailLoading ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Order Modal ───────────────────────────────────────────────── */}
      {cancelModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => e.target === e.currentTarget && !cancelLoading && setCancelModal(false)}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <XCircle size={18} className="text-danger" />
                  Cancel Order #{order.orderID}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCancelModal(false)}
                  disabled={cancelLoading}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  This action cannot be undone. Please provide a reason for
                  cancellation.
                  {order.paymentStatus === 'authorized' && (
                    <span className="d-block mt-1 text-warning">
                      The Stripe authorization hold will be voided automatically.
                    </span>
                  )}
                </p>
                <label className="form-label fw-semibold">
                  Cancellation Reason <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="e.g. Customer requested cancellation, product out of stock…"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={cancelLoading}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setCancelModal(false)}
                  disabled={cancelLoading}
                >
                  Keep Order
                </button>
                <button
                  type="button"
                  className="btn btn-danger d-flex align-items-center gap-2"
                  onClick={handleCancelOrder}
                  disabled={cancelLoading || !cancelReason.trim()}
                >
                  {cancelLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <XCircle size={15} />
                  )}
                  {cancelLoading ? 'Cancelling…' : 'Cancel Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Stripe Refund Modal ─────────────────────────────────────────────── */}
      {refundModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => e.target === e.currentTarget && !refundLoading && setRefundModal(false)}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <DollarSign size={18} />
                  Issue Stripe Refund
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setRefundModal(false)}
                  disabled={refundLoading}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-warning d-flex align-items-start gap-2 py-2 mb-3">
                  <AlertCircle size={16} className="flex-shrink-0 mt-1" />
                  <span className="small">
                    This will immediately process a refund through Stripe and cannot be undone.
                  </span>
                </div>

                {/* Full refund toggle */}
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="fullRefundCheck"
                    checked={refundForm.fullRefund}
                    onChange={(e) => {
                      const full = e.target.checked;
                      setRefundForm((f) => ({
                        ...f,
                        fullRefund: full,
                        amount: full ? parseFloat(order.totalAmount || 0).toFixed(2) : f.amount,
                      }));
                    }}
                    disabled={refundLoading}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="fullRefundCheck">
                    Full Refund ({formatCurrency(order.totalAmount)})
                  </label>
                </div>

                {/* Amount input */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Refund Amount <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      min="0.01"
                      step="0.01"
                      max={parseFloat(order.totalAmount || 0)}
                      value={refundForm.amount}
                      onChange={(e) =>
                        setRefundForm((f) => ({ ...f, amount: e.target.value }))
                      }
                      disabled={refundLoading || refundForm.fullRefund}
                    />
                  </div>
                  <div className="form-text">
                    Maximum: {formatCurrency(order.totalAmount)}
                  </div>
                </div>

                {/* Reason */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={refundForm.reason}
                    onChange={(e) =>
                      setRefundForm((f) => ({ ...f, reason: e.target.value }))
                    }
                    disabled={refundLoading}
                  >
                    <option value="requested_by_customer">Customer Request</option>
                    <option value="duplicate">Duplicate Order</option>
                    <option value="fraudulent">Fraudulent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setRefundModal(false)}
                  disabled={refundLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning d-flex align-items-center gap-2"
                  onClick={handleIssueRefund}
                  disabled={refundLoading}
                >
                  {refundLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <DollarSign size={15} />
                  )}
                  {refundLoading ? 'Processing…' : `Refund ${formatCurrency(refundForm.amount)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Record Payment Modal ─────────────────────────────────────────────── */}
      {paymentModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => e.target === e.currentTarget && !paymentLoading && setPaymentModal(false)}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <DollarSign size={18} />
                  Record Payment
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPaymentModal(false)}
                  disabled={paymentLoading}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <label className="form-label fw-semibold">
                      Amount <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        value={paymentForm.amount}
                        onChange={(e) =>
                          setPaymentForm((f) => ({ ...f, amount: e.target.value }))
                        }
                        disabled={paymentLoading}
                        autoFocus
                      />
                    </div>
                    {remaining > 0 && (
                      <div className="form-text">
                        Balance remaining: {formatCurrency(remaining)}
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label fw-semibold">
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm((f) => ({ ...f, paymentDate: e.target.value }))
                      }
                      disabled={paymentLoading}
                    />
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label fw-semibold">
                      Payment Method <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={paymentForm.paymentMethod}
                      onChange={(e) =>
                        setPaymentForm((f) => ({ ...f, paymentMethod: e.target.value }))
                      }
                      disabled={paymentLoading}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label fw-semibold">
                      Reference # <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Check #, wire ref…"
                      value={paymentForm.referenceNumber}
                      onChange={(e) =>
                        setPaymentForm((f) => ({ ...f, referenceNumber: e.target.value }))
                      }
                      disabled={paymentLoading}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Note <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Internal note…"
                      value={paymentForm.note}
                      onChange={(e) =>
                        setPaymentForm((f) => ({ ...f, note: e.target.value }))
                      }
                      disabled={paymentLoading}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPaymentModal(false)}
                  disabled={paymentLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleRecordPayment}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <DollarSign size={15} />
                  )}
                  {paymentLoading ? 'Saving…' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
